import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CacheProvider } from '@emotion/react';
import { Close, Fullscreen, OpenInNew } from '@mui/icons-material';
import { Box, Drawer, IconButton, ThemeProvider, useTheme } from '@mui/material';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import DrawerContext from 'components/Drawer/draggable-drawer/DraggableDrawer.context';
import { useZoom } from 'context/ZoomContext';
import { APP_DRAGGABLE_DRAWER_HEIGHT_VAR, MIN_DRAWER_HEIGHT } from './DraggableDrawer.constants';
import DraggableDrawerProps from './DraggableDrawer.props';
import { DrawerContentWrapper, DrawerHeader, DrawerHeaderContent, DrawerSizeBar } from './DraggableDrawer.styles';
import useDrawerPopout from './hooks/useDrawerPopout';

let globalHeight = MIN_DRAWER_HEIGHT;
let lastPagePath = '';

const DRAWER_HEIGHT_MULTIPLIER = 10;
const EXPANDED_HEIGHT_RATIO = 0.6;

const DraggableDrawer = ({
  children,
  headerContent,
  sidePanel,
  contentWrapperSx,
  headerContentSx,
  expandRef,
  ...rest
}: DraggableDrawerProps) => {
  const location = useLocation();
  const theme = useTheme();
  const [drawerHeight, setDrawerHeight] = useState(globalHeight);
  const [isDragging, setIsDragging] = useState(false);
  const initialMouseYRef = useRef<number>(0);
  const initialHeightRef = useRef<number>(0);

  const { zoomLevel } = useZoom();

  const {
    consumePopoutActionFromMain,
    handoffPopoutActionToMain,
    isPopped,
    popoutHeight,
    popoutContainerRef,
    popoutEmotionCache,
    reopenPopoutIfNeeded,
    handlePopout,
    handlePopIn,
    closeIfNavigating,
  } = useDrawerPopout({ zoomLevel });

  // Publish drawer height as a CSS variable for external layout consumers
  useEffect(() => {
    globalHeight = drawerHeight;
    try {
      const height = isPopped ? 0 : Math.max(drawerHeight, MIN_DRAWER_HEIGHT);
      document.documentElement.style.setProperty(APP_DRAGGABLE_DRAWER_HEIGHT_VAR, `${height}px`);
    } catch {
      // no-op
    }
  }, [drawerHeight, isPopped]);

  const expandDrawer = useCallback(() => {
    setDrawerHeight(prevHeight => {
      if (prevHeight <= MIN_DRAWER_HEIGHT) {
        return Math.max(
          Math.floor(window.innerHeight * EXPANDED_HEIGHT_RATIO),
          MIN_DRAWER_HEIGHT * DRAWER_HEIGHT_MULTIPLIER
        );
      }
      return prevHeight;
    });
  }, []);

  useEffect(() => {
    if (expandRef) {
      // eslint-disable-next-line no-param-reassign
      expandRef.current = expandDrawer;
    }
  }, [expandDrawer, expandRef]);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      initialMouseYRef.current = event.clientY;
      initialHeightRef.current = drawerHeight;
      setIsDragging(true);
    },
    [drawerHeight]
  );

  const handleMouseUp = useCallback(() => setIsDragging(false), []);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (isDragging) {
        event.preventDefault();
        const deltaY = initialMouseYRef.current - event.clientY;
        const newHeight = initialHeightRef.current + deltaY;
        setDrawerHeight(newHeight < MIN_DRAWER_HEIGHT ? MIN_DRAWER_HEIGHT : newHeight);
      }
    },
    [isDragging]
  );

  const handleFullScreen = useCallback(() => {
    setDrawerHeight(window.innerHeight);
  }, []);

  const handleMinimize = useCallback(() => {
    setDrawerHeight(MIN_DRAWER_HEIGHT);
  }, []);

  const handleAuxiliaryOpen = useCallback(() => {
    setDrawerHeight(prev =>
      prev === MIN_DRAWER_HEIGHT ? MIN_DRAWER_HEIGHT * DRAWER_HEIGHT_MULTIPLIER : MIN_DRAWER_HEIGHT
    );
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const expandedHeight = MIN_DRAWER_HEIGHT * DRAWER_HEIGHT_MULTIPLIER;
    if (e.altKey && e.key === 'ArrowUp') {
      e.preventDefault();
      setDrawerHeight(prev => expandedHeight + prev);
    } else if (e.altKey && (e.key === 'ArrowDown' || e.key === 'c')) {
      e.preventDefault();
      setDrawerHeight(MIN_DRAWER_HEIGHT);
    } else if (e.altKey && e.key === 'o') {
      e.preventDefault();
      const availableHeight = window.innerHeight - 100;
      setDrawerHeight(availableHeight < MIN_DRAWER_HEIGHT ? MIN_DRAWER_HEIGHT : availableHeight);
    }
  }, []);

  // Reset drawer height on route change and close the popout if configured
  useEffect(() => {
    if (lastPagePath !== location.pathname) {
      lastPagePath = location.pathname;
      globalHeight = MIN_DRAWER_HEIGHT;
      setDrawerHeight(MIN_DRAWER_HEIGHT);
      closeIfNavigating();
    }
  }, [location.pathname, closeIfNavigating]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Reset the CSS variable on unmount
  useEffect(
    () => () => {
      try {
        document.documentElement.style.setProperty(APP_DRAGGABLE_DRAWER_HEIGHT_VAR, `${MIN_DRAWER_HEIGHT}px`);
      } catch {
        // ignore
      }
    },
    []
  );

  const isContentVisible = isPopped || drawerHeight !== MIN_DRAWER_HEIGHT;

  const drawerContent = (
    <>
      {!isPopped && (
        <DrawerSizeBar
          onMouseDown={handleMouseDown}
          onDoubleClick={handleAuxiliaryOpen}
          data-testid="drawer-resize-bar"
        />
      )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          // Clip here; put overflow on DrawerContentWrapper only so wide XLSX (and other content)
          // scrolls under the header instead of moving the whole row horizontally.
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <DrawerHeader>
          <DrawerHeaderContent
            isPopped={isPopped}
            sx={headerContentSx}
            onClick={() => !isPopped && drawerHeight === MIN_DRAWER_HEIGHT && handleAuxiliaryOpen()}>
            {headerContent}
          </DrawerHeaderContent>

          {isPopped ? (
            <IconButton onClick={handlePopIn} title="Pop in" data-testid="drawer-pop-in-button">
              <OpenInNew style={{ transform: 'rotate(180deg)' }} />
            </IconButton>
          ) : (
            <IconButton onClick={handlePopout} title="Pop out to window" data-testid="drawer-pop-out-button">
              <OpenInNew />
            </IconButton>
          )}

          {!isPopped && drawerHeight !== window.innerHeight && (
            <IconButton onClick={handleFullScreen} data-testid="drawer-fullscreen-button">
              <Fullscreen />
            </IconButton>
          )}
          {!isPopped && drawerHeight !== MIN_DRAWER_HEIGHT && (
            <IconButton onClick={handleMinimize} data-testid="drawer-minimize-button">
              <Close />
            </IconButton>
          )}
        </DrawerHeader>

        {isContentVisible &&
          (sidePanel ? (
            // Hoist the side panel OUT of the content (tab) swap: it stays mounted
            // in this left column while {children} (the active tab) renders on the
            // right. Shares the same row in both the popout and the main drawer.
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row' }}>
              {sidePanel}
              {/* The active tab's strip (notes/sheets/docs) renders as an absolutely
                  positioned AppBar. Make THIS right column its containing block - a
                  non-scrolling, position:relative ANCESTOR of the scroll area, so the
                  strip still floats (never scrolls/clips) but is bounded to the column
                  and starts to the RIGHT of the side panel, instead of spanning the
                  full drawer and covering the panel's header/bulk actions. The offset
                  reset pins it just under the drawer header, where it sits without a
                  side panel. */}
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  // Reset the strip's built-in offset (it was tuned to clear the
                  // drawer header back when its containing block was the full drawer
                  // paper). Now this column is the containing block, so pin it flush
                  // to the top. The extra class raises specificity above the AppBar's
                  // own `&.MuiAppBar-positionAbsolute` rule, which is injected later.
                  '& .MuiAppBar-root.MuiAppBar-positionAbsolute': { top: 0, marginTop: 0 },
                }}>
                <DrawerContentWrapper sx={contentWrapperSx}>{children}</DrawerContentWrapper>
              </Box>
            </div>
          ) : (
            <DrawerContentWrapper sx={contentWrapperSx}>{children}</DrawerContentWrapper>
          ))}
      </div>
    </>
  );

  const contextValue = useMemo(
    () => ({
      consumePopoutActionFromMain,
      drawerHeight,
      expandDrawer,
      handoffPopoutActionToMain,
      isPopped,
      popoutHeight,
      reopenPopoutIfNeeded,
      setDrawerHeight,
    }),
    [
      consumePopoutActionFromMain,
      drawerHeight,
      expandDrawer,
      handoffPopoutActionToMain,
      isPopped,
      popoutHeight,
      reopenPopoutIfNeeded,
    ]
  );

  const popoutPortal =
    isPopped && popoutContainerRef.current && popoutEmotionCache
      ? createPortal(
        <CacheProvider value={popoutEmotionCache}>
          <ThemeProvider theme={theme}>
            <div
              style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fff' }}
              data-testid="drawer-popout-content">
              {drawerContent}
            </div>
          </ThemeProvider>
        </CacheProvider>,
        popoutContainerRef.current
      )
      : null;

  return (
    <div onMouseMove={!isPopped ? (handleMouseMove as unknown as React.MouseEventHandler<HTMLDivElement>) : undefined}>
      <DrawerContext.Provider value={contextValue}>
        {popoutPortal ?? (
          <Drawer
            open
            id="draggable-drawer"
            anchor="bottom"
            variant="persistent"
            sx={{
              '& .MuiDrawer-paper': {
                height: drawerHeight,
                overflowY: 'hidden',
                zIndex: 1100,
                display: 'flex',
                flexDirection: 'column',
                ...(!isPopped && {
                  left: 0,
                  width: '100%',
                }),
              },
            }}
            {...rest}>
            {drawerContent}
          </Drawer>
        )}
      </DrawerContext.Provider>
    </div>
  );
};

export default DraggableDrawer;
